import { AFFIX_DEFINITIONS, ITEM_SKILLS } from '@renavora/content';
import type {
  Affix,
  AffixDefinition,
  AffixId,
  Item,
  ItemSkill,
  JobDefinition,
  Rarity,
  Slot,
} from '@renavora/shared';

const AFFIX_BY_ID = new Map<AffixId, AffixDefinition>(
  AFFIX_DEFINITIONS.map((def) => [def.id, def]),
);

const UNIQUE_NAME: Readonly<Record<string, string>> = {
  'unique.juggernaut': 'Titan Regent',
  'unique.ironwall': 'Ironwall Aegis',
  'unique.berserker': 'Berserker Thirst',
  'unique.executioner': 'Executioner Sigil',
  'unique.crimson-thirst': 'Crimson Thirst',
  'unique.frost-shroud': 'Frost Shroud',
  'unique.assassin-cloak': 'Nightglass Cloak',
  'unique.guardian-oath': 'Guardian Oath',
};

const SET_NAME: Readonly<Record<string, string>> = {
  'set.aegis-vow': 'Sacred Aegis Vow',
  'set.seven-phase-crown': 'Seven-Phase Crown',
  'set.overflowing-grace': 'Overflowing Grace',
  'set.afterimage': 'Afterimage',
  'set.feast-of-the-dead': 'Feast of the Dead',
  'set.dragon-scale': 'Dragonscale Covenant',
  'set.rune-empower': 'Rune Empowerment',
  'set.magus-union': 'Magus Union',
  'set.inviolable-sanctuary': 'Inviolable Sanctuary',
  'set.mark-accumulation': 'Mark Accumulation',
  'set.plague-spread': 'Plague Spread',
  'set.fortress-architect': 'Fortress Architect',
  'set.killing-chain': 'Killing Chain',
  'set.conflagration': 'Conflagration',
  'set.absolute-zero': 'Absolute Zero',
  'set.abyss': 'Abyss',
  'set.last-stand': 'Last Stand',
  'set.bulwark': 'Bulwark',
  'set.attrition': 'War of Attrition',
  'set.tri-relic': 'Tri-Relic',
  'set.reactive-engine': 'Reactive Threshold Engine',
  'set.fortune-seeker': 'Fortune Seeker',
};

const BASE_KIND_LABEL: Readonly<Record<string, string>> = {
  sword: 'Sword',
  axe: 'Axe',
  spear: 'Spear',
  bow: 'Bow',
  staff: 'Staff',
  dagger: 'Dagger',
  mace: 'Mace',
  scythe: 'Scythe',
  wand: 'Wand',
  helmet: 'Helm',
  chest: 'Armor',
  gloves: 'Gauntlets',
  boots: 'Greaves',
  belt: 'Belt',
  amulet: 'Amulet',
  ring: 'Ring',
  cloak: 'Cloak',
  shield: 'Shield',
  focus: 'Focus',
  quiver: 'Quiver',
  plate: 'Plate',
};

const SLOT_LABEL: Readonly<Record<Slot, string>> = {
  weaponMain: 'Main Hand',
  offHand: 'Off Hand',
  head: 'Head',
  chest: 'Chest',
  hands: 'Hands',
  boots: 'Boots',
  belt: 'Belt',
  amulet: 'Amulet',
  ring1: 'Ring I',
  ring2: 'Ring II',
  cloak: 'Cloak',
};

const RARITY_LABEL: Readonly<Record<Rarity, string>> = {
  normal: 'Common',
  magic: 'Magic',
  rare: 'Rare',
  unique: 'Unique',
  set: 'Set',
};

const ELEMENT_LABEL: Readonly<Record<string, string>> = {
  physical: 'Physical',
  fire: 'Fire',
  ice: 'Ice',
  lightning: 'Lightning',
  earth: 'Earth',
  light: 'Light',
  dark: 'Dark',
};

const STATUS_LABEL: Readonly<Record<string, string>> = {
  bleed: 'Bleed',
  armor_break: 'Armor Break',
  crush: 'Crush',
  fighting_spirit: 'Fighting Spirit',
  mighty: 'Mighty',
  stance: 'Stance',
  burn: 'Burn',
  scald: 'Scald',
  parch: 'Parch',
  heat_aura: 'Heat Aura',
  fire_circle: 'Fire Circle',
  ignite: 'Ignite',
  chill: 'Chill',
  freeze: 'Freeze',
  frostbind: 'Frostbind',
  ice_film: 'Ice Film',
  ice_wall: 'Ice Wall',
  composure: 'Composure',
  shock: 'Shock',
  paralyze: 'Paralysis',
  overload: 'Overload',
  charge: 'Charge',
  overcharge: 'Overcharge',
  flash: 'Flash',
  pressure: 'Pressure',
  petrify: 'Petrify',
  brittle: 'Brittle',
  harden: 'Harden',
  earth_guard: 'Earth Guard',
  shelter: 'Shelter',
  daze: 'Daze',
  dazzle: 'Dazzle',
  stigma: 'Stigma',
  seal: 'Seal',
  blessing: 'Blessing',
  divine_grace: 'Divine Grace',
  cleanse: 'Cleanse',
  poison: 'Poison',
  erosion: 'Erosion',
  curse: 'Curse',
  absorb: 'Absorb',
  blood_oath: 'Blood Oath',
  shadow_veil: 'Shadow Veil',
  'mark.physical': 'Physical Mark',
  'mark.fire': 'Fire Mark',
  'mark.ice': 'Ice Mark',
  'mark.lightning': 'Lightning Mark',
  'mark.earth': 'Earth Mark',
  'mark.light': 'Light Mark',
  'mark.dark': 'Dark Mark',
  kill_streak: 'Kill Streak',
  'stat-boost.atk-bonus': 'Attack Boost',
  'stat-boost.crit-rate': 'Critical Chance Boost',
  'stat-boost.crit-dmg': 'Critical Damage Boost',
  'stat-boost.armor': 'Armor Boost',
  'stat-boost.hp-max': 'Maximum HP Boost',
  'stat-boost.status-resist': 'Status Resistance Boost',
  'stat-boost.move-speed': 'Move Speed Boost',
  'stat-boost.accuracy': 'Accuracy Boost',
  'stat-boost.dodge': 'Dodge Boost',
};

const CREATURE_LABEL: Readonly<Record<string, string>> = {
  humanoid: 'Humanoids',
  beast: 'Beasts',
  undead: 'Undead',
  demon: 'Demons',
  dragon: 'Dragons',
  elemental: 'Elementals',
  mech: 'Constructs',
  plant: 'Wildgrowth',
};

const OFFENSE_LABEL: Readonly<Record<string, string>> = {
  attack_speed: 'Attack Speed',
  crit_chance: 'Critical Chance',
  crit_damage: 'Critical Damage',
  accuracy: 'Accuracy',
  cooldown_rate: 'Cooldown Recovery',
  dot_damage: 'Damage over Time',
};

const PROJECTILE_LABEL: Readonly<Record<string, string>> = {
  extra: 'Extra Projectile',
  pierce: 'Projectile Pierce',
  speed: 'Projectile Speed',
  range: 'Projectile Range',
};

const DEFENSE_LABEL: Readonly<Record<string, string>> = {
  max_hp: 'Maximum HP',
  armor_pct: 'Armor',
  all_resist: 'All Resistances',
  shield_damage_reduction: 'Shield Guard',
};

const UTILITY_LABEL: Readonly<Record<string, string>> = {
  move_speed: 'Move Speed',
  dodge: 'Dodge',
  block_chance: 'Block Chance',
};

const SUSTAIN_LABEL: Readonly<Record<string, string>> = {
  life_on_hit: 'Life on Hit',
  hp_regen: 'HP Regeneration',
};

function titleFromId(value: string): string {
  return value
    .replace(/^(affix|item|base|set|unique|status)\./, '')
    .split(/[._-]+/u)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function debugIdsEnabled(search = globalThis.location?.search ?? ''): boolean {
  return new URLSearchParams(search).get('debugIds') === '1';
}

export function slotDisplayName(slot: Slot): string {
  return SLOT_LABEL[slot];
}

export function rarityDisplayName(rarity: Rarity): string {
  return RARITY_LABEL[rarity];
}

export function jobDisplayName(jobOrId: JobDefinition | string): string {
  const id = typeof jobOrId === 'string' ? jobOrId : jobOrId.id;
  return titleFromId(id);
}

export function setDisplayName(setId: string): string {
  return SET_NAME[setId] ?? titleFromId(setId);
}

export function deriveBaseName(baseId: string): string {
  const parts = baseId.split('.');
  if (parts.length < 4) return titleFromId(baseId);
  const baseKind = parts[parts.length - 2]!;
  const tier = parts[parts.length - 1]!;
  return `${BASE_KIND_LABEL[baseKind] ?? titleFromId(baseKind)} T${tier}`;
}

export function itemDisplayName(item: Item): string {
  const uniqueName = item.uniqueId !== undefined ? UNIQUE_NAME[item.uniqueId] : undefined;
  return uniqueName ?? deriveBaseName(item.baseId);
}

export function itemSkillDisplayName(skill: ItemSkill): string {
  const template = ITEM_SKILLS[skill.templateId];
  if (template?.name !== undefined) {
    return template.name.replace(/^Vanilla /u, '');
  }
  return titleFromId(skill.templateId);
}

export function skillDisplayName(skillId: string): string {
  return titleFromId(skillId.replace(/^ult\./u, ''));
}

export function statusDisplayName(statusId: string): string {
  return STATUS_LABEL[statusId] ?? titleFromId(statusId);
}

export interface AffixDisplay {
  kind: 'Prefix' | 'Suffix' | 'Implicit';
  name: string;
  value: string;
  tier: string;
  debugId?: string;
}

export function affixDisplay(affix: Affix, search?: string): AffixDisplay {
  const def = AFFIX_BY_ID.get(affix.id);
  const debugId = debugIdsEnabled(search) ? affix.id : undefined;
  const display: AffixDisplay = {
    kind: affix.kind === 'prefix' ? 'Prefix' : affix.kind === 'suffix' ? 'Suffix' : 'Implicit',
    name: def === undefined ? titleFromId(affix.id) : affixDisplayName(def),
    value: def === undefined ? affix.values.join('/') : affixValueDisplay(def, affix),
    tier: `T${affix.tier}`,
  };
  return debugId === undefined ? display : { ...display, debugId };
}

function affixDisplayName(def: AffixDefinition): string {
  const parts = def.id.split('.');
  if (parts.length < 4) return titleFromId(def.id);
  const [, , family, key] = parts as [string, string, string, string];
  if (family === 'damage') return `${ELEMENT_LABEL[key] ?? titleFromId(key)} Damage`;
  if (family === 'penetration') return `${ELEMENT_LABEL[key] ?? titleFromId(key)} Penetration`;
  if (family === 'offense') return OFFENSE_LABEL[key] ?? titleFromId(key);
  if (family === 'proc') {
    if (key.endsWith('_chance')) {
      const status = key.replace(/_chance$/u, '');
      return `${STATUS_LABEL[status] ?? titleFromId(status)} Chance`;
    }
    if (key === 'ailment_duration') return 'Ailment Duration';
    if (key === 'ailment_potency') return 'Ailment Potency';
  }
  if (family === 'conditional') {
    if (key === 'execute_damage') return 'Execution Damage';
    if (key === 'vs_marked') return 'Marked Target Damage';
  }
  if (family === 'projectile') return PROJECTILE_LABEL[key] ?? titleFromId(key);
  if (family === 'defense') {
    if (key.startsWith('resist_')) {
      const element = key.replace(/^resist_/u, '');
      return `${ELEMENT_LABEL[element] ?? titleFromId(element)} Resistance`;
    }
    return DEFENSE_LABEL[key] ?? titleFromId(key);
  }
  if (family === 'status') {
    if (key === 'resist_all') return 'All Status Resistance';
    if (key.startsWith('resist_')) {
      const status = key.replace(/^resist_/u, '');
      return `${STATUS_LABEL[status] ?? titleFromId(status)} Resistance`;
    }
  }
  if (family === 'utility') return UTILITY_LABEL[key] ?? titleFromId(key);
  if (family === 'sustain') return SUSTAIN_LABEL[key] ?? titleFromId(key);
  if (family === 'slayer') return `Damage to ${CREATURE_LABEL[key] ?? titleFromId(key)}`;
  if (family === 'minion') return `Minion ${titleFromId(key)}`;
  if (family === 'placeable') return `Placeable ${titleFromId(key)}`;
  if (family === 'support' && key === 'healing_power') return 'Healing Power';
  return titleFromId(def.id);
}

function affixValueDisplay(def: AffixDefinition, affix: Affix): string {
  const first = affix.values[0] ?? 0;
  if (def.valueKind === 'percent-add' || def.valueKind === 'percent-mul') {
    return `+${first}%`;
  }
  if (def.valueKind === 'integer') {
    return `+${first}`;
  }
  return `+${first}`;
}
